/**
 * Zustand store for live dashboard state.
 *
 * Handles all 13 ServerEvent types from HedgePair DOs.
 * Maintains global alert feed and activity log.
 */

import { create } from "zustand";
import type { ServerEvent } from "@pfh/shared";

export interface AccountLive {
  accountId: string;
  equity: number;
  balance: number;
  marginLevel: number | null;
  /** ACCOUNT-WIDE floating P&L from the bot's broker footer (sum of
   *  every open position on the account, regardless of symbol). DO NOT
   *  render this as a per-symbol-pair leg P&L — use the
   *  `selectPairLegOpenPnl` selector to derive a per-symbol value from
   *  the pair's positions slice. Confusing the two was the bleed bug
   *  where Match-Trader's account total appeared under MT5's symbol. */
  accountAggregateOpenPnl: number;
  closedPnl: number | null;
  online: boolean | null;
  updatedAt: number;
}

/** Pair-level sleep state, fed by `pair:sleep-state` events. Null
 *  until the first event arrives. */
export interface PairSleepLive {
  accountPairId: string;
  mode: "auto" | "manual";
  manualState: "awake" | "asleep";
  /** "orphan" = at least one leg's Brave didn't actually close on the
   *  last sleep attempt. Dashboard surfaces a Retry / Force-kill UI. */
  currentState: "awake" | "asleep" | "partial" | "unknown" | "orphan";
  autoSleepProtected: boolean;
  reason: string | null;
  legStates: Record<string, "awake" | "asleep" | "unknown" | "orphan">;
}

export interface PositionLive {
  accountId: string;
  ticket: number;
  symbol: string;
  side: "buy" | "sell";
  lots: number;
  entry: number;
  stopLoss: number | null;
  takeProfit: number | null;
  openPnl: number;
  updatedAt: number;
}

interface PairLive {
  pairId: string;
  lastDecision: {
    tier: "haiku" | "sonnet";
    action: string;
    complianceStatus: string | null;
    reason: string;
    confidence: number | null;
    createdAt: number;
  } | null;
  alerts: Array<{ type: string; payload: unknown; at: number }>;
  connectionStatus: "connecting" | "open" | "closed";
  /** Latest per-account snapshot keyed by accountId. Updated on every
   *  account:tick, position:update, and account:status frame. */
  accounts: Record<string, AccountLive>;
  /** Latest per-ticket position snapshot keyed by `accountId:ticket`.
   *  `heartbeat` events with lots=0 are treated as flat and removed. */
  positions: Record<string, PositionLive>;
  /** Pair-level sleep state, fed by pair:sleep-state events. Null
   *  until the first event lands. */
  sleep: PairSleepLive | null;
}

interface ActivityItem {
  id: string;
  pairId: string;
  type: string;
  summary: string;
  at: number;
}

interface DashboardStore {
  pairs: Record<string, PairLive>;
  activityFeed: ActivityItem[];
  unreadAlertCount: number;

  ingest(pairId: string, event: ServerEvent): void;
  resetPair(pairId: string): void;
  setConnectionStatus(pairId: string, status: "connecting" | "open" | "closed"): void;
  markAlertsRead(): void;
}

let activityCounter = 0;

function emptyPairLive(pairId: string): PairLive {
  return {
    pairId,
    lastDecision: null,
    alerts: [],
    connectionStatus: "closed",
    accounts: {},
    positions: {},
    sleep: null,
  };
}

export const useDashboardStore = create<DashboardStore>((set) => ({
  pairs: {},
  activityFeed: [],
  unreadAlertCount: 0,

  ingest(pairId, event) {
    set((state) => {
      const current = state.pairs[pairId] ?? {
        ...emptyPairLive(pairId),
        connectionStatus: "open" as const,
      };

      const activityItem: ActivityItem = {
        id: `${++activityCounter}`,
        pairId,
        type: event.type,
        summary: getEventSummary(event),
        at: Date.now(),
      };

      const newFeed = [activityItem, ...state.activityFeed.slice(0, 99)];

      switch (event.type) {
        case "hedge:decision":
          return {
            pairs: {
              ...state.pairs,
              [pairId]: {
                ...current,
                lastDecision: {
                  tier: event.payload.tier,
                  action: event.payload.action,
                  complianceStatus: event.payload.complianceStatus,
                  reason: event.payload.reason,
                  confidence: event.payload.confidence,
                  createdAt: event.payload.createdAt,
                },
              },
            },
            activityFeed: newFeed,
          };

        case "account:tick": {
          const tick = event.payload;
          const prev = current.accounts[tick.account];
          const nextAccount: AccountLive = {
            accountId: tick.account,
            equity: tick.equity,
            balance: tick.balance,
            marginLevel: tick.marginLevel,
            // tick.openPnl is the broker footer's account-wide total —
            // store it as `accountAggregateOpenPnl` so per-symbol pair
            // cards can't accidentally render it as a leg P&L.
            accountAggregateOpenPnl: tick.openPnl,
            closedPnl: prev?.closedPnl ?? null,
            online: prev?.online ?? true,
            updatedAt: tick.ts,
          };
          return {
            pairs: {
              ...state.pairs,
              [pairId]: {
                ...current,
                accounts: { ...current.accounts, [tick.account]: nextAccount },
              },
            },
            activityFeed: newFeed,
          };
        }

        case "position:update": {
          const p = event.payload;
          const accountId = p.accountId;
          const prevAccount = current.accounts[accountId];
          const nextAccount: AccountLive = {
            accountId,
            equity: p.equity,
            balance: p.balance,
            marginLevel: p.marginLevel,
            // p.openPnl on a position frame is the account-wide total
            // the bot reads from the broker footer, NOT the per-position
            // P&L (that lives in p — the position row — below). Store
            // as the account aggregate; per-symbol leg P&L is derived
            // from the positions slice via selectPairLegOpenPnl.
            accountAggregateOpenPnl: p.openPnl,
            closedPnl: p.closedPnl ?? prevAccount?.closedPnl ?? null,
            online: prevAccount?.online ?? true,
            updatedAt: p.timestampMs,
          };
          const nextPositions = { ...current.positions };
          // Always flush any ticket-0 ghost — observers sometimes race a
          // mid-render DOM and emit ticket=0 before bailing, and legacy
          // entries may still live in the store. Costs nothing on each
          // tick, and guarantees the ghost never sticks.
          delete nextPositions[`${accountId}:0`];

          const ticketValid =
            typeof p.ticket === "number" && Number.isFinite(p.ticket) && p.ticket > 0;

          // Only zero-ticket frames are skipped (they carry no real
          // position). `heartbeat` is the MT5 observer's default event
          // type and carries the full ticket/lots/entry/sl/tp row — we
          // must upsert it, otherwise the store never learns about
          // the live position.
          if (ticketValid) {
            const key = `${accountId}:${p.ticket}`;
            const isFlat = p.event === "close" || p.lots === 0;
            if (isFlat) {
              delete nextPositions[key];
            } else {
              nextPositions[key] = {
                accountId,
                ticket: p.ticket,
                symbol: p.symbol,
                side: p.side,
                lots: p.lots,
                entry: p.entry,
                stopLoss: p.stopLoss,
                takeProfit: p.takeProfit,
                openPnl: p.openPnl,
                updatedAt: p.timestampMs,
              };
            }
          }
          return {
            pairs: {
              ...state.pairs,
              [pairId]: {
                ...current,
                accounts: { ...current.accounts, [accountId]: nextAccount },
                positions: nextPositions,
              },
            },
            activityFeed: newFeed,
          };
        }

        case "account:status": {
          const s = event.payload;
          const prev = current.accounts[s.account];
          const nextAccount: AccountLive = prev
            ? { ...prev, online: s.online, updatedAt: s.lastSeen }
            : {
                accountId: s.account,
                equity: 0,
                balance: 0,
                marginLevel: null,
                accountAggregateOpenPnl: 0,
                closedPnl: null,
                online: s.online,
                updatedAt: s.lastSeen,
              };
          return {
            pairs: {
              ...state.pairs,
              [pairId]: {
                ...current,
                accounts: { ...current.accounts, [s.account]: nextAccount },
              },
            },
            activityFeed: newFeed,
          };
        }

        case "pair:sleep-state": {
          const s = event.payload;
          const sleep: PairSleepLive = {
            accountPairId: s.accountPairId,
            mode: s.mode,
            manualState: s.manualState,
            currentState: s.currentState,
            autoSleepProtected: s.autoSleepProtected,
            reason: s.reason,
            legStates: s.legStates,
          };
          return {
            pairs: {
              ...state.pairs,
              [pairId]: { ...current, sleep },
            },
            activityFeed: newFeed,
          };
        }

        case "alert:drawdown":
        case "alert:breach":
        case "alert:news":
        case "alert:compliance":
        case "alert:discount":
        case "alert:availability":
        case "alert:rules-updated":
        case "alert:rules-conflict":
          return {
            pairs: {
              ...state.pairs,
              [pairId]: {
                ...current,
                alerts: [
                  { type: event.type, payload: event.payload, at: Date.now() },
                  ...current.alerts.slice(0, 49),
                ],
              },
            },
            activityFeed: newFeed,
            unreadAlertCount: state.unreadAlertCount + 1,
          };

        default:
          return {
            ...state,
            activityFeed: newFeed,
          };
      }
    });
  },

  resetPair(pairId) {
    set((state) => {
      const { [pairId]: _, ...rest } = state.pairs;
      return { pairs: rest };
    });
  },

  setConnectionStatus(pairId, status) {
    set((state) => {
      const current = state.pairs[pairId] ?? emptyPairLive(pairId);
      return {
        pairs: {
          ...state.pairs,
          [pairId]: { ...current, connectionStatus: status },
        },
      };
    });
  },

  markAlertsRead() {
    set({ unreadAlertCount: 0 });
  },
}));

/**
 * Per-pair-leg open P&L. Sums every position in the pair's positions
 * slice whose accountId matches the requested leg. This is what the
 * per-symbol pair card SHOULD show — NOT the leg's account-aggregate
 * (which would include positions from other symbol pairs the same
 * account is hedging on, the bleed bug).
 *
 * Returns `null` if the pair slice doesn't exist yet (live data hasn't
 * landed). Caller decides whether to render "—" or fall back.
 */
export function selectPairLegOpenPnl(
  state: DashboardStore,
  pairId: string,
  accountId: string,
): number | null {
  const pair = state.pairs[pairId];
  if (!pair) return null;
  let sum = 0;
  let any = false;
  for (const pos of Object.values(pair.positions)) {
    if (pos.accountId !== accountId) continue;
    sum += pos.openPnl;
    any = true;
  }
  // No positions on this leg right now is a meaningful "0", not "no data".
  // Caller can distinguish the two via `selectHasLiveAccount` if needed.
  return any ? sum : 0;
}

/**
 * True iff we've received any live frame for this account in this pair
 * slice (account:tick / position:update / account:status). Used by
 * pair-detail to decide whether to show a live-PnL number or a "—"
 * placeholder when the WS is still warming up.
 */
export function selectHasLiveAccount(
  state: DashboardStore,
  pairId: string,
  accountId: string,
): boolean {
  return state.pairs[pairId]?.accounts[accountId] != null;
}

function getEventSummary(event: ServerEvent): string {
  switch (event.type) {
    case "hedge:decision":
      return `${event.payload.tier} → ${event.payload.action} (${event.payload.complianceStatus ?? "ok"})`;
    case "hedge:executed":
      return `Executed: ${event.payload.action}`;
    case "position:update":
      return `Position ${event.payload.event}: ${event.payload.symbol} ${event.payload.side} ${event.payload.lots}L`;
    case "pair:state":
      return "Pair state updated";
    case "account:status":
      return `Account ${event.payload.account} ${event.payload.online ? "online" : "offline"}`;
    case "pair:sleep-state":
      return `Pair ${event.payload.accountPairId.slice(0, 8)} bot ${event.payload.currentState} (${event.payload.mode})`;
    case "alert:drawdown":
      return `Drawdown ${event.payload.kind}: ${event.payload.pct.toFixed(1)}%`;
    case "alert:breach":
      return `Breach: ${event.payload.kind}`;
    case "alert:news":
      return `News: ${event.payload.event} (${event.payload.currency})`;
    case "alert:compliance":
      return `Compliance: ${event.payload.violation}`;
    default:
      return event.type;
  }
}
